import { useIsRestoring } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { useDelegacoesRecebidas } from '../hooks/useDelegacoes';
import { useAtuacao } from '../lib/delegacao/atuacao';
import { classifyError } from '../lib/errors';
import { useSubscription, type SubscriptionData } from '../hooks/useSubscription';
import { useConvitesPendentes } from '../hooks/useConvitesPendentes';
import { Skeleton } from './ui/skeleton';

// Escritórios e Delegações ficam abertos sem assinatura: é por eles que o membro
// convidado (secretário, estagiário) aceita o convite e passa a atuar na conta do
// titular — docs/PLANO_DELEGACAO.md.
const EXEMPT_PATHS = ['/app/assinatura', '/app/configuracoes', '/app/escritorios', '/app/delegacoes'];
const EXEMPT_ROLES = ['admin', 'suporte'];
const BLOCKED_STATUSES = ['EXPIRADA', 'CANCELADA', 'PENDING'];

function isTrialExpired(status: string, trialFim: string | null): boolean {
  if (status !== 'TRIAL' || !trialFim) return false;
  return new Date(trialFim) <= new Date();
}

function isBlockedSubscription(subscription: SubscriptionData | null | undefined): boolean {
  if (!subscription) return true;
  if (BLOCKED_STATUSES.includes(subscription.status)) return true;
  return isTrialExpired(subscription.status, subscription.trial_fim);
}


interface SubscriptionGateProps {
  children: React.ReactNode;
}

export function SubscriptionGate({ children }: SubscriptionGateProps) {
  const location = useLocation();
  const { user } = useAuth();
  const isRestoring = useIsRestoring();
  const { data: subscription, isLoading, isFetching, isError, error } = useSubscription();
  const isBlocked = isBlockedSubscription(subscription);
  const atuacao = useAtuacao();
  const recebidas = useDelegacoesRecebidas();
  // Sem assinatura e com convite pendente: a tela de Escritórios mostra o convite (isenta).
  const pendentes = useConvitesPendentes(isBlocked && !isLoading);

  // Admin não precisa de assinatura
  if (user?.role && EXEMPT_ROLES.includes(user.role)) {
    return <>{children}</>;
  }

  // Na conta do titular vale o plano DELE, e quem decide é o backend (gate por
  // `dono_da_conta`): titular sem assinatura responde 403 nas próprias rotas.
  if (atuacao) {
    return <>{children}</>;
  }

  // Rotas isentas não são bloqueadas
  const isExempt = EXEMPT_PATHS.some((p) => location.pathname.startsWith(p));
  if (isExempt) {
    return <>{children}</>;
  }

  // Cortesia ativa libera acesso independente de subscription — e ANTES do
  // skeleton de revalidação: quem já tem acesso por cortesia não pode ter o
  // app desmontado por um refetch. Com trial expirado (isBlocked) + cortesia,
  // qualquer consumidor de useSubscription montado dentro do gate
  // (refetchOnMount: 'always') entrava em loop: montar → refetch → skeleton
  // desmonta → fetch resolve → remonta → refetch → ... A revogação real de
  // cortesia continua coberta: o dado novo chega sem cortesia e cai nos
  // branches de bloqueio abaixo.
  // A vaga paga pelo escritório (docs/PLANO_COMPARTILHAMENTO_ESCRITORIO.md §29) libera como a
  // cortesia: o app inteiro, pelo plano do Proprietário.
  if (subscription?.cortesia || subscription?.escritorio_que_paga) {
    return <>{children}</>;
  }

  // Aguardar carregamento — mostrar skeleton em vez de children para evitar flash
  // Aguardar carregamento inicial e revalidação de estados bloqueantes
  // para não redirecionar com cache stale.
  if (isRestoring || isLoading || (isFetching && isBlocked)) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  // 401: interceptor já está redirecionando para /login — não liberar acesso
  if (isError && error && classifyError(error).isAuthError) {
    return null;
  }

  // Erro real (500, timeout, rede) — liberar acesso para não bloquear
  // indevidamente. O backend é a fonte de verdade via require_active_access.
  if (isError) {
    return <>{children}</>;
  }

  // Sem subscription, status bloqueado, ou trial expirado → paywall. Quem atua na
  // conta de alguém vai para a escolha da conta, não para a venda de um plano de que
  // não precisa.
  if (isBlocked) {
    // Entrando pela conta do titular (link do e-mail): o shell troca o contexto já.
    if (recebidas.isLoading || new URLSearchParams(location.search).has('atuando')) {
      return (
        <div className="space-y-4 p-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
        </div>
      );
    }
    if ((recebidas.data?.length ?? 0) > 0) {
      return <Navigate to="/app/delegacoes" replace />;
    }
    if (pendentes.isLoading) {
      return (
        <div className="space-y-4 p-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
        </div>
      );
    }
    if ((pendentes.data?.length ?? 0) > 0) {
      return <Navigate to="/app/escritorios" replace />;
    }
    return <Navigate to="/app/assinatura" replace />;
  }

  return <>{children}</>;
}
